import { useEffect, useRef, useState } from 'react';

/**
 * useReveal — fires once when the element enters the viewport.
 * Returns a ref and a boolean that flips to `true` on first intersection.
 *
 * Designed for subtle scroll-in animations — keep transforms minimal
 * (translate + opacity only) to retain the institutional, restrained feel.
 *
 * Respects `prefers-reduced-motion`: when reduced-motion is requested the
 * element is reported visible immediately and the animation is skipped.
 */
export function useReveal({ threshold = 0.12, rootMargin = '0px 0px -10% 0px' } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // SSR / reduced motion → immediate visible, no observer needed.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setVisible(true);
      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return [ref, visible];
}

/**
 * useCountUp — animates an integer from 0 to `target` over `duration` ms
 * once `active` is true (typically tied to a useReveal flag). Uses
 * requestAnimationFrame + ease-out for a polished proof-stat feel.
 *
 * Returns the current displayed value.
 */
export function useCountUp(target, { duration = 1200, active = true } = {}) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    if (typeof target !== 'number' || Number.isNaN(target) || target <= 0) return undefined;

    // Respect reduced motion — set final value immediately.
    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setValue(target);
      return undefined;
    }

    // Strict-Mode safe: scoped `cancelled` flag prevents the cleaned-up
    // run from updating state after unmount.
    let cancelled = false;
    const start = performance.now();
    let rafId = 0;
    const tick = (now) => {
      if (cancelled) return;
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setValue(target * eased);
      if (t < 1) rafId = requestAnimationFrame(tick);
      else setValue(target);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [target, duration, active]);

  return value;
}

export default useReveal;
